import { IconButton } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StatusCode, WebsocketTypes } from '../../api/enumerations';
import { getAllWorkOrders, getTopWorkOrders } from '../../api/workOrders';
import {
  GetAllWorkOrderDataProps,
  GetTopWorkOrdersData,
  GetWorkOrdersParams,
} from '../../api/workOrders/types';
import { Search } from '../../components/Search';
import Snackbar from '../../components/Snackbar';
import { TitleTypography } from '../../components/UI/Typography';
import { Constants } from '../../constants/home';
import {
  IconAddMS,
  IconAssignmentMS,
  IconCheckCircleMS,
  IconCloseMS,
  IconDesktopWindowsMS,
  IconEngineeringMS,
} from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { WebsocketContext } from '../../context/websocketMessage';
import { useDebounce } from '../../hooks/useDebounce';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { BankSearch } from './BankSearch';
import { ViewModeToggle } from './components/ViewModeToggle';
import { useViewMode, ViewMode } from './hooks/useViewMode';
import { useWorkOrdersFlat } from './hooks/useWorkOrdersFlat';
import {
  GridContainer,
  OSButton,
  PlaceholderBox,
  ScrollTypography,
  StyledToast,
  TitleBox,
  TitleGroup,
  ToastTypography,
  TVMode,
} from './styles';
import { GalleryView } from './views/GalleryView';
import { MapView } from './views/MapView';
import { PipelineView } from './views/PipelineView';
import { TableView } from './views/TableView';

export interface IWebsocketMessageData {
  osId: number;
  unreadCount: number;
}

const safeParseWebsocketMessage = (
  raw: string
): { type?: string; payload?: { reference_number?: number } } | null => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export function Home(): JSX.Element {
  const [pipelineData, setPipelineData] = useState<
    GetAllWorkOrderDataProps | GetTopWorkOrdersData
  >();
  const [searchField, setSearchField] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [filters, setFilters] = useState<GetWorkOrdersParams>();

  const { openSnackbar, isHiveOwner } = useContext(GlobalContext);
  const { websocketMessage } = useContext(WebsocketContext);
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(searchField);

  const enabledViewModes: ViewMode[] = isHiveOwner
    ? ['pipeline', 'table']
    : ['pipeline'];

  const { viewMode, setViewMode } = useViewMode({
    enabledModes: enabledViewModes,
  });

  const flatViewActive =
    isHiveOwner &&
    (viewMode === 'table' || viewMode === 'map' || viewMode === 'gallery');

  const flatQuery = useWorkOrdersFlat({
    enabled: flatViewActive,
    searchField: debouncedSearch,
    filters,
  });

  const getTopWorkOrdersCallback = useCallback(async () => {
    if (!firstLoad) {
      return;
    }

    setLoading(true);

    try {
      const topWorkOrders = await getTopWorkOrders();

      if (topWorkOrders.detail.description) {
        throw new Error(topWorkOrders.detail.description);
      }

      if (topWorkOrders.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (topWorkOrders.data) {
        setPipelineData(topWorkOrders.data);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstLoad]);

  useEffect(() => {
    getTopWorkOrdersCallback();
  }, [getTopWorkOrdersCallback]);

  useEffect(() => {
    if (!websocketMessage) return;
    const messageData = safeParseWebsocketMessage(websocketMessage.data);
    if (!messageData) return;
    if (messageData.type !== WebsocketTypes.AVM_PROCESSED) return;

    const refNumber = messageData.payload?.reference_number;
    if (typeof refNumber === 'number') {
      setToastMessage(`OS ${refNumber} finalizou o cálculo.`);
      setOpenToast(true);
    }

    if (flatViewActive) {
      flatQuery.refetch();
    } else {
      setFirstLoad(true);
      getTopWorkOrdersCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocketMessage]);

  const getWorkOrdersByFilters = useCallback(async () => {
    setLoading(true);

    const params: GetWorkOrdersParams = {};

    if (searchField.length > 0) params.search = searchField;
    if (filters?.uf) params.uf = filters?.uf;
    if (filters?.city) params.city = filters?.city;
    if (filters?.licenseCompanyId)
      params.licenseCompanyId = filters?.licenseCompanyId;
    if (filters?.startDate) params.startDate = filters?.startDate;
    if (filters?.endDate) params.endDate = filters?.endDate;

    try {
      const response = await getAllWorkOrders(params);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (response.data) {
        setPipelineData(response.data);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchField, filters]);

  useEffect(() => {
    if (debouncedSearch.length > 0 || filters) {
      getWorkOrdersByFilters();
    } else if (debouncedSearch.length === 0 && !filters) {
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters]);

  const handleNewOs = (): void => {
    navigate('/home/property/new');
  };

  const switchToPresentationMode = (): void => {
    navigate('/home/presentation-mode');
  };

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      {IconCloseMS}
    </IconButton>
  );

  const renderActiveView = (): JSX.Element => {
    if (viewMode === 'pipeline') {
      return (
        <PipelineView
          pipelineData={pipelineData}
          pipelineLoading={loading}
          isSearching={searchField.length > 0}
        />
      );
    }
    if (viewMode === 'table') {
      return <TableView data={flatQuery.data} loading={flatQuery.loading} />;
    }
    if (viewMode === 'map') {
      return (
        <MapView
          data={flatQuery.data}
          loading={flatQuery.loading}
          onSwitchToTable={() => setViewMode('table')}
        />
      );
    }
    if (viewMode === 'gallery') {
      return <GalleryView data={flatQuery.data} loading={flatQuery.loading} />;
    }
    return (
      <PlaceholderBox>
        {IconEngineeringMS}
        <p>essa visualização está em construção</p>
      </PlaceholderBox>
    );
  };

  return (
    <GridContainer>
      <TitleBox>
        <TitleGroup>
          <TitleTypography>
            {IconAssignmentMS}
            {isHiveOwner ? Constants.bankTitle : Constants.title}
          </TitleTypography>
          {isHiveOwner && (
            <ViewModeToggle
              viewMode={viewMode}
              onChange={setViewMode}
              enabledModes={enabledViewModes}
            />
          )}
        </TitleGroup>
        <OSButton onClick={handleNewOs}>
          {IconAddMS}
          {Constants.buttonAdd}
        </OSButton>
        {!isHiveOwner && (
          <TVMode onClick={switchToPresentationMode}>
            {IconDesktopWindowsMS}
            {Constants.TVMode}
          </TVMode>
        )}
        {isHiveOwner ? (
          <BankSearch
            setSearchField={setSearchField}
            setFilters={setFilters}
            searchLoading={loading}
            isFiltersActive={!!filters}
          />
        ) : (
          <Search setSearchField={setSearchField} />
        )}
      </TitleBox>
      {renderActiveView()}
      <ScrollTypography>-</ScrollTypography>
      {openSnackbar && <Snackbar />}
      <StyledToast
        open={openToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleClose}
        action={action}
        message={
          <ToastTypography>
            {IconCheckCircleMS}
            {toastMessage}
          </ToastTypography>
        }
      />
    </GridContainer>
  );
}
